import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";

import Modal from "@material-tailwind/react/Modal";
import ModalHeader from "@material-tailwind/react/ModalHeader";
import ModalBody from "@material-tailwind/react/ModalBody";
import ModalFooter from "@material-tailwind/react/ModalFooter";
import Button from "@material-tailwind/react/Button";

import {
  aksiApproval,
  approvalSelector,
  approveList,
} from "../setup/approval/approvalSlice";
import {
  cutiTransaksiSelector,
  getSpecCuti,
} from "../../features/hrd/transaksi/cuti/cutiTransaksiSlice";
import {
  getSpecTiket,
  tiketTransaksiSelector,
} from "../../features/hrd/transaksi/tiket/tiketTransaksiSlice";
import {
  getSpecResign,
  resignTransaksiSelector,
} from "../../features/hrd/transaksi/resign/resignTransaksiSlice";
import {
  getSpecPhk,
  phkTransaksiSelector,
} from "../../features/hrd/transaksi/phk/phkTransaksiSlice";
import {
  getSpecPelatihan,
  pelatihanTransaksiSelector,
} from "../../features/hrd/transaksi/pelatihan/pelatihanTransaksiSlice";
import {
  getSpecLembur,
  lemburTransaksiSelector,
} from "../../features/hrd/transaksi/lembur/lemburTransaksiSlice";
import {
  getSpecIzin,
  izinTransaksiSelector,
} from "../../features/hrd/transaksi/izin/izinTransaksiSlice";
import {
  claimPengobatanTransaksiSelector,
  getSpecClaimPengobatan,
} from "../../features/hrd/transaksi/claim-pengobatan/claimPengobatanTransaksiSlice";
import Loading from "../../components/loading/Loading";
import { useNavigate } from "react-router-dom";
import {
  getSpecSakit,
  sakitTransaksiSelector,
} from "../hrd/transaksi/sakit/sakitTransaksiSlice";
import {
  getSpecPromosi,
  promosiTransaksiSelector,
} from "../hrd/transaksi/promosi/promosiTransaksiSlice";
import {
  getSpecKontrakKerja,
  kontrakKerjaTransaksiSelector,
} from "../hrd/transaksi/kontrak-kerja/kontrakKerjaTransaksiSlice";
import {
  DetailPerjalananDinas,
} from "../hrd/transaksi/perjalanan-dinas/PerjalananDinasPage";
import {
  getSpecLaporan,
  getSpecPerjalananDinas,
  getSpecUbah,
} from "../hrd/transaksi/perjalanan-dinas/perjalananDinasApi";
import {
  DetailLaporan,
  DetailUbah,
} from "../hrd/transaksi/perjalanan-dinas/PerjalananDinasFase2";

// ---------------------------------------------------------------------------
// API helpers (tanpa toast — khusus untuk memuat daftar). Setiap sumber
// dinormalisasi menjadi array baris. Pola sama seperti hooks/useApprovalCount.js
// ---------------------------------------------------------------------------
const API_URL = process.env.REACT_APP_API_URL;

const getToken = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.Token ?? "";
  } catch {
    return "";
  }
};
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });
const apiGet = (path, params) =>
  axios
    .get(API_URL + path, { headers: authHeader(), params })
    .then((r) => r.data?.data ?? [])
    .catch(() => []);
const apiPost = (path, body = {}) =>
  axios
    .post(API_URL + path, body, { headers: authHeader() })
    .then((r) => r.data?.data ?? [])
    .catch(() => []);

// Berbeda dari apiPost di atas yang menelan error jadi []: approval borongan
// HARUS bisa membedakan "gagal" dari "tidak ada hasil", jadi errornya dilempar.
const apiPostRaw = (path, body = {}) =>
  axios
    .post(API_URL + path, body, { headers: authHeader() })
    .then((r) => r.data);

const PR_PARAM = {
  start: 0,
  count: 100,
  search: "",
  BisnisUnitID: "U",
  Menu: "Approval",
};

// Backend approval HRD mengirim tanggal "DD-MM-YYYY" (TO_CHAR di GetApprovalListModel).
// new Date() membacanya sbg MM-DD-YYYY: tgl >12 jadi "Invalid Date", tgl 1–12 tertukar
// hari↔bulan (09-12 → 9 Des). Format itu dirakit manual; format lain (ISO, dll) tetap
// lewat new Date() seperti semula.
const fmtDate = (v) => {
  if (!v) return "-";
  const m = String(v).trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
  const d = m ? new Date(+m[3], +m[2] - 1, +m[1]) : new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toDateString();
};
const dash = (v) => (v === null || v === undefined || v === "" ? "-" : v);
// Rupiah berpemisah ribuan; "-" bila kosong/non-angka. Nominal dari backend string.
const fmtRp = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? "-" : "Rp " + n.toLocaleString("id-ID");
};

// Parse tanggal lembur dari SpecLemburData. Toleran terhadap beberapa format
// yang mungkin dikirim backend: ISO / "YYYY-MM-DD HH:MM:SS" dan "DD-MM-YYYY HH:MM".
const parseLemburTgl = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
  m = str.match(/^(\d{2})-(\d{2})-(\d{4})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5], +(m[6] || 0));
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

// Durasi lembur dari selisih mulai–akhir. Format sama seperti kolom list backend:
// "H jam M menit" (menit disembunyikan bila 0).
const formatDurasiLembur = (mulai, akhir) => {
  const a = parseLemburTgl(mulai);
  const b = parseLemburTgl(akhir);
  if (!a || !b) return "-";
  const menitTotal = Math.max(0, Math.round((b - a) / 60000));
  const jam = Math.floor(menitTotal / 60);
  const menit = menitTotal % 60;
  return menit > 0 ? `${jam} jam ${menit} menit` : `${jam} jam`;
};

// Urutan tampil semua tab: DESCENDING berdasarkan ID transaksi (terbaru di atas).
// numeric:true wajib — perbandingan leksikal murni menaruh "PA099" di atas
// "PA0100". Selalu menyalin array dulu: rows berasal dari state redux/useState,
// dan Array.sort mengurutkan di tempat.
const sortDesc = (rows, key) =>
  [...rows].sort((a, b) =>
    String(b?.[key] ?? "").localeCompare(String(a?.[key] ?? ""), "id", {
      numeric: true,
      sensitivity: "base",
    })
  );

// Kolom kas/bank dipakai ulang oleh 6 sumber Finance. Keterangan & Nominal
// diambil dari header CashJournal (bukan CashJournalList) — keempat endpoint
// approval kas/bank sudah mengirimkannya, jadi aman dipasang global di sini.
const cashColumns = (idHeader) => [
  { header: idHeader, cell: (i) => dash(i.CashJournalID) },
  { header: "Tanggal", cell: (i) => fmtDate(i.Tanggal) },
  { header: "Dari/Kepada", cell: (i) => dash(i.DariKepada) },
  { header: "Bisnis Unit", cell: (i) => dash(i.NamaBisnisUnit) },
  {
    header: "Jenis Transaksi",
    cell: (i) => `${dash(i.JenisTransaksi)} - ${dash(i.JenisCashJournal)}`,
  },
  { header: "Keterangan", cell: (i) => dash(i.Keterangan) },
  { header: "Nominal", cell: (i) => fmtRp(i.Nominal) },
];
const goToPattycash = (i, navigate) =>
  navigate(
    "/finance/approval/pattycash/" + (i.CashJournalID ?? "").replaceAll("/", "-")
  );

// ---------------------------------------------------------------------------
// Konfigurasi sumber approval selain HRD.
// fetch   : () => Promise<array>. Akses dijaga BACKEND (CheckAuth modul +
//           filter per-user Nik/rantai approval); yang tak berhak balas non-2xx
//           → apiGet/apiPost kembalikan [] → tab disembunyikan filter-antrean.
//           Sengaja TANPA gate otoritas di frontend agar tak mismatch dengan
//           modul yang benar-benar dicek backend (key sering beda).
// columns : [{ header, cell:(item) => node }]
// onRowClick(item, navigate)
// ---------------------------------------------------------------------------
const NON_HRD_SOURCES = [
  {
    id: "po",
    sortKey: "PurchaseOrderID",
    label: "Purchase Order Jasa",
    otoritas: "PurchaseRequisition",
    fetch: () => apiGet("Procurement/PurchaseOrder/table/approval"),
    columns: [
      { header: "Purchase Order ID", cell: (i) => dash(i.PurchaseOrderID) },
      { header: "Tanggal", cell: (i) => fmtDate(i.DatePR) },
      { header: "Bisnis Unit", cell: (i) => dash(i.NamaBisnisUnit) },
      { header: "Requester", cell: (i) => dash(i.Requester) },
      { header: "Tipe", cell: (i) => dash(i.Tipe) },
    ],
    onRowClick: (i, navigate) =>
      navigate("/procurment/approval/purchase-order", {
        state: {
          PurchaseRequisitionID: i.PurchaseRequisitionID,
          PurchaseOrderID: i.PurchaseOrderID,
          // Supplier/PRComparison yang BENAR-BENAR jadi PO ini -- satu PR bisa
          // melahirkan banyak PO dari supplier berbeda, jadi ini wajib dikirim
          // supaya perhitungan nominal termin tak salah nyasar ke PO lain
          // dari PR yang sama (insiden PO1623, lihat HrdInputGenerator.js).
          PRComparisonID: i.PRComparisonID,
        },
      }),
  },
  {
    id: "pr",
    sortKey: "PurchaseRequisitionID",
    label: "Purchase Requisition",
    otoritas: "PurchaseRequisition",
    fetch: () =>
      apiPost("Procurement/PurchaseRequisition/table/approval", PR_PARAM),
    columns: [
      {
        header: "Purchase Requisition ID",
        cell: (i) => dash(i.PurchaseRequisitionID),
      },
      { header: "Tanggal", cell: (i) => fmtDate(i.DatePR) },
      { header: "Bisnis Unit", cell: (i) => dash(i.NamaBisnisUnit) },
      { header: "Requester", cell: (i) => dash(i.Requester) },
      { header: "Remarks", cell: (i) => dash(i.Remarks) },
      { header: "Tipe", cell: (i) => dash(i.Tipe) },
    ],
    onRowClick: (i, navigate) =>
      navigate("/procurment/approval/purchase-requisition", {
        state: { PurchaseRequisitionID: i.PurchaseRequisitionID },
      }),
    // Approve borongan mengirim ItemDibatalkan kosong = setujui seluruh item apa
    // adanya, persis seperti membuka dokumen lalu Approve tanpa mencoret apa pun.
    //
    // Tab "pr-os" memakai jenis yang SAMA. Sempat dikira butuh jalur tersendiri
    // karena ada ApprovalPurchaseRequisitionOutStock di backend — fungsi itu
    // ternyata nol pemanggil (kode mati), dan kedua tab mengarah ke halaman
    // approval yang sama dengan endpoint yang sama.
    bulk: { jenis: "pr", idOf: (i) => i.PurchaseRequisitionID },
  },
  {
    id: "pr-os",
    sortKey: "PurchaseRequisitionID",
    label: "Purchase Requisition (Out Stock)",
    otoritas: "PurchaseRequisition",
    fetch: () =>
      apiPost(
        "Procurement/PurchaseRequisition/table/approvaloutstock",
        PR_PARAM
      ),
    columns: [
      {
        header: "Purchase Requisition ID",
        cell: (i) => dash(i.PurchaseRequisitionID),
      },
      { header: "Tanggal", cell: (i) => fmtDate(i.DatePR) },
      { header: "Bisnis Unit", cell: (i) => dash(i.NamaBisnisUnit) },
      { header: "Requester", cell: (i) => dash(i.Requester) },
      { header: "Remarks", cell: (i) => dash(i.Remarks) },
      { header: "Tipe", cell: (i) => dash(i.Tipe) },
    ],
    onRowClick: (i, navigate) =>
      navigate("/procurment/approval/purchase-requisition", {
        state: { PurchaseRequisitionID: i.PurchaseRequisitionID },
      }),
    // Antrean Aldi yang sebenarnya ada di SINI (Out Stock), bukan di tab "pr"
    // yang kosong untuknya. Jalur approve-nya identik dengan "pr".
    bulk: { jenis: "pr", idOf: (i) => i.PurchaseRequisitionID },
  },
  {
    id: "pc",
    // Endpoint approval PC TIDAK mengirim PRComparisonID (lihat
    // GetPRComparisonApproval di backend) — urutkan pakai PRCApprovalID yang
    // memang ada, kalau tidak seluruh kunci kosong dan sort jadi no-op.
    sortKey: "PRCApprovalID",
    // Hak approve PC ditentukan rantai approval (DetailMasterApproval + Nik) di
    // backend, BUKAN otoritas modul "PriceComparison" (approver sah bisa saja
    // punya PriceComparison=0). Endpoint sudah per-user, jadi otoritas dibuat
    // null (selalu fetch) — filter-antrean yang menyembunyikan bila kosong.
    label: "Price Comparison",
    otoritas: null,
    fetch: () => apiPost("Procurement/PriceComparison/table/approval", {}),
    columns: [
      { header: "Id. Price Comparison", cell: (i) => dash(i.PRComparisonID) },
      { header: "Tanggal", cell: (i) => fmtDate(i.DatePR) },
      { header: "Id. MSR", cell: (i) => dash(i.PurchaseRequisitionID) },
      { header: "Requester", cell: (i) => dash(i.Requester) },
      { header: "Remarks", cell: (i) => dash(i.Remarks) },
    ],
    onRowClick: (i, navigate) =>
      navigate("/procurment/approval/price-comparison", {
        state: {
          PurchaseRequisitionID: i.PurchaseRequisitionID,
          PRComparisonID: i.PRComparisonID,
          PRCApprovalID: i.PRCApprovalID,
        },
      }),
    // PRComparisonID di baris antrean berisi MPRComparisonID — ID yang sama yang
    // dikirim layar approval. Jalur borongan tidak menulis PilihanBeli sama sekali.
    bulk: { jenis: "pc", idOf: (i) => i.PRComparisonID },
  },
  {
    id: "ga",
    sortKey: "GoodsAcceptanceID",
    label: "Penerimaan Barang",
    otoritas: "GoodsAcceptance",
    fetch: () => apiGet("Procurement/GoodsAcceptance/approval"),
    columns: [
      {
        header: "Penerimaan Barang ID",
        cell: (i) => dash(i.GoodsAcceptanceID),
      },
      { header: "Tanggal", cell: (i) => fmtDate(i.Tanggal) },
      { header: "Nama Penerima", cell: (i) => dash(i.NamaPenerima) },
      { header: "Keterangan", cell: (i) => dash(i.Keterangan) },
    ],
    onRowClick: (i, navigate) =>
      navigate("/gudang/approval/penerimaan-barang", {
        state: { GoodsAcceptanceID: i.GoodsAcceptanceID },
      }),
  },
  {
    id: "gau",
    sortKey: "GoodsAcceptanceUserID",
    label: "Penerimaan Barang User",
    otoritas: "GoodsAccepteanceUser",
    fetch: () => apiGet("Procurement/GoodsAcceptanceUser/approval"),
    columns: [
      {
        header: "Goods Acceptance User ID",
        cell: (i) => dash(i.GoodsAcceptanceUserID),
      },
      { header: "Tanggal", cell: (i) => fmtDate(i.Tanggal) },
      { header: "Nama Penerima", cell: (i) => dash(i.NamaPenerima) },
      { header: "Keterangan", cell: (i) => dash(i.Keterangan) },
    ],
    onRowClick: (i, navigate) =>
      navigate("/gudang/approval/penerimaan-barang-user", {
        state: { GoodsAcceptanceUserID: i.GoodsAcceptanceUserID },
      }),
  },
  {
    id: "ge",
    sortKey: "GoodsExpenditureID",
    label: "Pengeluaran Barang",
    otoritas: "GoodsExpenditure",
    fetch: () => apiGet("Procurement/GoodsExpenditure/approval"),
    columns: [
      {
        header: "Pengeluaran Barang ID",
        cell: (i) => dash(i.GoodsExpenditureID),
      },
      { header: "Tanggal", cell: (i) => fmtDate(i.Tanggal) },
      { header: "Nama Peminta", cell: (i) => dash(i.NamaPeminta) },
      { header: "Keterangan", cell: (i) => dash(i.Keterangan) },
    ],
    onRowClick: (i, navigate) =>
      navigate("/gudang/approval/pengeluaran-barang", {
        state: { GoodsExpenditureID: i.GoodsExpenditureID },
      }),
  },
  {
    id: "return",
    sortKey: "GoodsReturnID",
    label: "Return Barang",
    otoritas: "GoodsReturn",
    fetch: () => apiGet("Procurement/GoodsReturn/approval"),
    columns: [
      { header: "Goods Return ID", cell: (i) => dash(i.GoodsReturnID) },
      { header: "Tanggal", cell: (i) => fmtDate(i.Tanggal) },
      { header: "Nama Penerima", cell: (i) => dash(i.NamaPenerima) },
      { header: "Keterangan", cell: (i) => dash(i.Keterangan) },
    ],
    onRowClick: (i, navigate) =>
      navigate("/gudang/approval/return-barang", {
        state: { GoodsReturnID: i.GoodsReturnID },
      }),
  },
  {
    id: "kendaraan",
    sortKey: "TKendaraanID",
    label: "Transaksi Kendaraan",
    otoritas: "TKendaraan",
    fetch: () => apiGet("Procurement/Kendaraan/approval"),
    columns: [
      { header: "Transaksi Kendaraan ID", cell: (i) => dash(i.TKendaraanID) },
      { header: "Unit", cell: (i) => dash(i.NamaGeneralServiceUnit) },
      { header: "Keterangan", cell: (i) => dash(i.Keterangan) },
      { header: "Tanggal", cell: (i) => fmtDate(i.Tanggal) },
    ],
    onRowClick: (i, navigate) =>
      navigate("/equipment/kendaraan/kendaraan/" + i.TKendaraanID),
  },
  {
    id: "cj",
    sortKey: "CashJournalID",
    label: "Pembayaran Uang Muka",
    otoritas: "Permohonan_Pembayaran_Non_PO",
    fetch: () => apiGet("Accounting/CashJournal/approval"),
    columns: cashColumns("ID. Pembayaran Uang Muka"),
    onRowClick: (i, navigate) =>
      navigate(
        "/finance/approval/form-kas-bank/" +
          (i.CashJournalID ?? "").replaceAll("/", "-")
      ),
    // Approval final di sini MENERBITKAN JURNAL. Backend menyusun payload dari
    // baris DB, jadi angka yang dijurnal persis yang tersimpan. Satu hal yang
    // hilang dibanding membuka dokumen: approver tak bisa mengisi No. Cek.
    bulk: { jenis: "cj", idOf: (i) => i.CashJournalID },
  },
  {
    id: "jurnal",
    sortKey: "AccountingID",
    label: "Jurnal",
    otoritas: "Jurnal",
    fetch: () => apiGet("Accounting/Approval"),
    columns: [
      { header: "ID. Jurnal", cell: (i) => dash(i.AccountingID) },
      { header: "Keterangan", cell: (i) => dash(i.Keterangan) },
      { header: "Tanggal", cell: (i) => fmtDate(i.Tanggal) },
    ],
    // AccountingID format baru ({5digit}/GM/{unit}/{MM}/{YYYY}) mengandung slash —
    // tanpa diubah ke dash, URL pecah jadi banyak segmen, route ":transid" tak
    // cocok, dan halaman blank putih. Backend menormalisasi dash→slash lagi.
    onRowClick: (i, navigate) =>
      navigate(
        "/accounting/approval/jurnal/" +
          (i.AccountingID ?? "").replaceAll("/", "-")
      ),
  },
  {
    id: "kas-ho",
    sortKey: "CashJournalID",
    label: "Pengeluaran Kas HO",
    otoritas: "Pengeluaran_Kas_HO",
    fetch: () => apiGet("Accounting/CashJournal/approvalp/U0001"),
    columns: cashColumns("ID. Pengeluaran Kas HO"),
    onRowClick: goToPattycash,
  },
  {
    id: "kas-so",
    sortKey: "CashJournalID",
    label: "Pengeluaran Kas SO",
    otoritas: "Pengeluaran_Kas_SO",
    fetch: () => apiGet("Accounting/CashJournal/approvalp/U0002"),
    columns: cashColumns("ID. Pengeluaran Kas SO"),
    onRowClick: goToPattycash,
  },
  {
    id: "pen-ho",
    sortKey: "CashJournalID",
    label: "Penerimaan Kas HO",
    otoritas: "Penerimaan_Kas_HO",
    fetch: () => apiGet("Accounting/CashJournal/approvalPenerimaan/U0001"),
    columns: cashColumns("ID. Penerimaan Kas HO"),
    onRowClick: goToPattycash,
  },
  {
    id: "pen-so",
    sortKey: "CashJournalID",
    label: "Penerimaan Kas SO",
    otoritas: "Penerimaan_Kas_SO",
    fetch: () => apiGet("Accounting/CashJournal/approvalPenerimaan/U0002"),
    columns: cashColumns("ID. Penerimaan Kas SO"),
    onRowClick: goToPattycash,
  },
  {
    id: "dana",
    sortKey: "CashJournalID",
    label: "Penerimaan Dana",
    otoritas: "Penerimaan_Dana",
    fetch: () => apiGet("Accounting/CashJournal/approvalDana/"),
    columns: cashColumns("ID. Penerimaan Dana"),
    onRowClick: goToPattycash,
  },
  {
    id: "nonpo",
    sortKey: "PembayaranAPID",
    label: "Pembayaran AP",
    otoritas: "Permohonan_Pembayaran_PO",
    fetch: () => apiGet("Accounting/PembayaranAP/approval"),
    // Keterangan & nominal ditampilkan seperti tab Pembayaran Uang Muka. Dua angka
    // sengaja dipisah: "Nominal" = nilai tagihan/PO, "Nilai Bayar" = yang benar-benar
    // dibayar (bisa lebih besar krn biaya tambahan). Approver perlu lihat keduanya.
    columns: [
      { header: "ID. Pembayaran AP", cell: (i) => dash(i.PembayaranAPID) },
      { header: "Tanggal", cell: (i) => fmtDate(i.Tanggal) },
      { header: "Dari/Kepada", cell: (i) => dash(i.DibayarKepada) },
      { header: "Jenis Pembayaran", cell: (i) => dash(i.JenisPembayaran) },
      { header: "Keterangan", cell: (i) => dash(i.Keterangan) },
      { header: "Nominal", cell: (i) => fmtRp(i.Nominal) },
      { header: "Nilai Bayar", cell: (i) => fmtRp(i.NilaiBayar) },
    ],
    onRowClick: (i, navigate) =>
      navigate(
        "/finance/approval/pembayaran-ap/" +
          (i.PembayaranAPID ?? "").replaceAll("/", "-")
      ),
    // Sama seperti Uang Muka: approval final menerbitkan jurnal. Penjaga
    // pembayaran-ganda per termin tetap berjalan karena tiap dokumen menempuh
    // ApprovalPembayaranAP yang sama.
    bulk: { jenis: "ap", idOf: (i) => i.PembayaranAPID },
  },
  {
    id: "jr",
    sortKey: "JRID",
    label: "Job Request",
    otoritas: "TActivity",
    fetch: () => apiGet("operational/jobrequest/approval"),
    columns: [
      { header: "ID. Jurnal", cell: (i) => dash(i.JRID) },
      { header: "Tanggal", cell: (i) => fmtDate(i.Tanggal) },
      { header: "Quatation No", cell: (i) => dash(i.QuatationNo) },
      { header: "SPK No", cell: (i) => dash(i.SPKNo) },
      { header: "Klient", cell: (i) => dash(i.KlienID) },
    ],
    onRowClick: (i, navigate) =>
      navigate("/operasional-activity-approval", { state: { JRID: i.JRID } }),
  },
];

const HRD_COLUMNS = [
  { header: "Jenis", cell: (i) => dash(i.Menu) },
  { header: "Tanggal", cell: (i) => fmtDate(i.Tanggal) },
  { header: "Nik", cell: (i) => dash(i.Nik) },
  { header: "Pemohon", cell: (i) => dash(i.Pemohon) },
  {
    header: "Transaction Approval ID",
    cell: (i) => dash(i.TransaksiApprovalID),
  },
];

// Kolom khusus antrean approval Lembur (tab terpisah dari HRD lain).
const LEMBUR_COLUMNS = [
  { header: "Jenis", cell: (i) => dash(i.Menu) },
  {
    header: "Transaction Approval ID",
    cell: (i) => dash(i.TransaksiApprovalID),
  },
  { header: "No SPL", cell: (i) => dash(i.NoSPL) },
  { header: "NIK", cell: (i) => dash(i.Nik) },
  { header: "Pemohon", cell: (i) => dash(i.Pemohon) },
  { header: "Keperluan Lembur", cell: (i) => dash(i.KeperluanLembur) },
  { header: "Tanggal", cell: (i) => dash(i.Tanggal) },
  { header: "Durasi Lembur", cell: (i) => dash(i.DurasiJam) },
];

// Kolom khusus antrean approval Promosi/Mutasi/Demosi (tab terpisah).
const PROMOSI_COLUMNS = [
  {
    header: "Transaction Approval ID",
    cell: (i) => dash(i.TransaksiApprovalID),
  },
  { header: "NIK", cell: (i) => dash(i.Nik) },
  { header: "Pemohon", cell: (i) => dash(i.Pemohon) },
  { header: "Tanggal Efektif", cell: (i) => dash(i.Tanggal) },
];

// Kolom khusus antrean approval Kontrak Kerja (tab terpisah).
const KONTRAK_KERJA_COLUMNS = [
  {
    header: "Transaction Approval ID",
    cell: (i) => dash(i.TransaksiApprovalID),
  },
  { header: "NIK", cell: (i) => dash(i.Nik) },
  { header: "Pemohon", cell: (i) => dash(i.Pemohon) },
  { header: "Tanggal Mulai", cell: (i) => dash(i.Tanggal) },
];

// Kolom khusus antrean approval Perjalanan Dinas (tab terpisah).
const PERJALANAN_DINAS_COLUMNS = [
  { header: "ID", cell: (i) => dash(i.TransaksiApprovalID) },
  { header: "NIK", cell: (i) => dash(i.Nik) },
  { header: "Pemohon", cell: (i) => dash(i.Pemohon) },
  { header: "Tanggal Berangkat", cell: (i) => dash(i.Tanggal) },
];

const PD_UBAH_COLUMNS = [
  { header: "ID", cell: (i) => dash(i.TransaksiApprovalID) },
  { header: "NIK", cell: (i) => dash(i.Nik) },
  { header: "Pemohon", cell: (i) => dash(i.Pemohon) },
  { header: "Berangkat (baru)", cell: (i) => dash(i.Tanggal) },
];
const PD_LAPORAN_COLUMNS = [
  { header: "ID", cell: (i) => dash(i.TransaksiApprovalID) },
  { header: "NIK", cell: (i) => dash(i.Nik) },
  { header: "Pemohon", cell: (i) => dash(i.Pemohon) },
  { header: "Perjalanan Selesai", cell: (i) => dash(i.Tanggal) },
];

// JenisTransaksi Promosi (kode tanpa lookup table, lihat backend
// models/hrd/promotion.go & PromosiPage.js — sumber kebenaran yang sama).
const JENIS_TRANSAKSI_PROMOSI = {
  1: "Promosi (naik jabatan)",
  2: "Mutasi (pindah lokasi tugas)",
  3: "Demosi (turun jabatan)",
};

// Kolom antrean approval Job Request V2. "TransaksiApprovalID" di sini berisi
// JrID (bukan ID mesin approval lama), "NoSPL" dipakai ulang untuk No. QO —
// mengikuti bentuk ApprovalToDo yang sudah ada agar tak perlu struct baru.
const JOB_REQUEST_COLUMNS = [
  { header: "No. JR", cell: (i) => dash(i.TransaksiApprovalID) },
  { header: "No. QO", cell: (i) => dash(i.NoSPL) },
  { header: "Klien", cell: (i) => dash(i.Pemohon) },
  { header: "Tgl Pekerjaan", cell: (i) => dash(i.Tanggal) },
];

// Kolom antrean approval Quotation. Sama seperti JR, bentuk ApprovalToDo dipakai
// ulang: "TransaksiApprovalID" berisi QoNo dan "NoSPL" dipakai untuk No. Kontrak.
const QUOTATION_COLUMNS = [
  { header: "No. QO", cell: (i) => dash(i.TransaksiApprovalID) },
  { header: "No. Kontrak", cell: (i) => dash(i.NoSPL) },
  { header: "Klien", cell: (i) => dash(i.Pemohon) },
  { header: "Tgl Dibuat", cell: (i) => dash(i.Tanggal) },
];

const ApprovalListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const approval = useSelector(approvalSelector);
  const cuti = useSelector(cutiTransaksiSelector);
  const tiket = useSelector(tiketTransaksiSelector);
  const resign = useSelector(resignTransaksiSelector);
  const phk = useSelector(phkTransaksiSelector);
  const pelatihan = useSelector(pelatihanTransaksiSelector);
  const lembur = useSelector(lemburTransaksiSelector);
  const izin = useSelector(izinTransaksiSelector);
  const claimPengobatan = useSelector(claimPengobatanTransaksiSelector);
  const sakit = useSelector(sakitTransaksiSelector);
  const promosi = useSelector(promosiTransaksiSelector);
  const kontrakKerja = useSelector(kontrakKerjaTransaksiSelector);

  // Tanpa gating otoritas di frontend: backend sudah menjaga akses lewat
  // CheckAuth(modul) + filter per-user (Nik/rantai approval). Endpoint yang tak
  // berhak balas non-2xx → apiGet/apiPost mengembalikan [] → tab tersembunyi
  // oleh filter-antrean. Menghindari mismatch key otoritas frontend vs backend.

  const [data, setData] = useState({}); // { [sourceId]: rows }
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const autoSelected = useRef(false);

  // Modal HRD
  const [currentMenu, setCurrentMenu] = useState("Cuti");
  const [currentID, setCurrentID] = useState("");
  const [showModalCuti, setShowModalCuti] = useState(false);
  const [showModalTiket, setShowModalTiket] = useState(false);
  const [showModalResign, setShowModalResign] = useState(false);
  const [showModalPhk, setShowModalPhk] = useState(false);
  const [showModalPelatihan, setShowModalPelatihan] = useState(false);
  const [showModalLembur, setShowModalLembur] = useState(false);
  const [showModalPromosi, setShowModalPromosi] = useState(false);
  const [showModalKontrakKerja, setShowModalKontrakKerja] = useState(false);
  const [showModalPerjalananDinas, setShowModalPerjalananDinas] = useState(false);
  const [perjalananDinasSpec, setPerjalananDinasSpec] = useState(null);
  // Perjalanan Dinas Fase 2: dokumen turunan yang sedang dibuka di modal approval.
  const [pdSub, setPdSub] = useState({ show: false, menu: "", spec: null });
  const [showModalIzin, setShowModalIzin] = useState(false);
  const [showModalClaimPengobatan, setShowModalClaimPengobatan] =
    useState(false);
  // Alasan/catatan approve-tolak untuk form approval Lembur.
  const [alasanApproval, setAlasanApproval] = useState("");

  // ── Approval borongan ──
  // Hak-nya ditentukan backend (GET /isBulkApprover); tak ada Nik di sini.
  const [canBulk, setCanBulk] = useState(false);
  const [bulkLimit, setBulkLimit] = useState(25);
  const [terpilih, setTerpilih] = useState([]); // ID dokumen yang dicentang
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkHasil, setBulkHasil] = useState(null); // [{ID, Berhasil, Pesan}]

  useEffect(() => {
    apiGet("isBulkApprover").then((d) => {
      setCanBulk(d?.CanBulkApprove === true);
      if (d?.Batas) setBulkLimit(d.Batas);
    });
  }, []);

  // Semua sumber selalu di-fetch; visibilitas ditentukan backend + filter-antrean.
  const visibleNonHrd = NON_HRD_SOURCES;
  const hrdVisible = true;

  const hrdRows = useMemo(
    () =>
      (approval.approveList ?? []).filter(
        (i) =>
          i.Menu !== "JR Activity" &&
          i.Menu !== "Lembur" &&
          i.Menu !== "Job Request" &&
          i.Menu !== "Quotation" &&
          i.Menu !== "Promosi" &&
          i.Menu !== "KontrakKerja" &&
          i.Menu !== "PerjalananDinas" &&
          i.Menu !== "PerjalananDinasUbah" &&
          i.Menu !== "PerjalananDinasLaporan"
      ),
    [approval.approveList]
  );

  // Job Request V2 dipisah ke tab sendiri. Aksinya TIDAK dilakukan di sini:
  // baris mengarah ke halaman detail JR, tempat approver bisa melihat hasil
  // activity yang sedang ia nilai sebelum menyetujui — approve dari daftar
  // berarti menyetujui tanpa melihat isinya. Mesin approval-nya juga berbeda
  // (aksiJrApprovalV2), bukan aksiApproval milik tab HRD.
  const jrRows = useMemo(
    () => (approval.approveList ?? []).filter((i) => i.Menu === "Job Request"),
    [approval.approveList]
  );

  // Quotation dipisah ke tab sendiri, alasan sama dengan JR: aksinya dilakukan di
  // halaman detail QO supaya approver melihat aktivitas, rate, dan minimum tagihan
  // sebelum menyetujui. Penjaganya ada di UpdateQoStatus, bukan di daftar ini.
  const qoRows = useMemo(
    () => (approval.approveList ?? []).filter((i) => i.Menu === "Quotation"),
    [approval.approveList]
  );

  // Lembur dipisah ke tab sendiri dengan kolom khusus (No SPL, Keperluan, Durasi).
  const lemburRows = useMemo(
    () => (approval.approveList ?? []).filter((i) => i.Menu === "Lembur"),
    [approval.approveList]
  );

  // Promosi/Mutasi/Demosi dipisah ke tab sendiri dengan modal & tombol
  // Approve/Tolak sendiri (pola sama Lembur) — sebelumnya jatuh ke tab HRD
  // generik tanpa handler klik yang berfungsi.
  const promosiRows = useMemo(
    () => (approval.approveList ?? []).filter((i) => i.Menu === "Promosi"),
    [approval.approveList]
  );

  // Kontrak Kerja — sama alasannya dengan Promosi di atas.
  const kontrakKerjaRows = useMemo(
    () => (approval.approveList ?? []).filter((i) => i.Menu === "KontrakKerja"),
    [approval.approveList]
  );

  // Perjalanan Dinas — tab sendiri (modal detail + alasan wajib, pola Kontrak Kerja).
  const perjalananDinasRows = useMemo(
    () => (approval.approveList ?? []).filter((i) => i.Menu === "PerjalananDinas"),
    [approval.approveList]
  );

  const pdUbahRows = useMemo(
    () => (approval.approveList ?? []).filter((i) => i.Menu === "PerjalananDinasUbah"),
    [approval.approveList]
  );
  const pdLaporanRows = useMemo(
    () => (approval.approveList ?? []).filter((i) => i.Menu === "PerjalananDinasLaporan"),
    [approval.approveList]
  );

  // Muat data: HRD lewat redux, sisanya lewat apiGet/apiPost — hanya yang diakses
  useEffect(() => {
    let cancelled = false;
    if (hrdVisible) dispatch(approveList({}));
    (async () => {
      const results = await Promise.all(
        visibleNonHrd.map((s) => s.fetch().then((rows) => [s.id, rows]))
      );
      if (cancelled) return;
      setData(Object.fromEntries(results));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Daftar tab yang tampil (HRD dulu bila diakses), lengkap dengan jumlah
  const tabs = useMemo(() => {
    const list = [];
    if (hrdVisible) {
      list.push({
        id: "hrd",
        label: "HRD",
        columns: HRD_COLUMNS,
        rows: sortDesc(hrdRows, "TransaksiApprovalID"),
        isHrd: true,
      });
      list.push({
        id: "lembur",
        label: "Lembur",
        columns: LEMBUR_COLUMNS,
        rows: sortDesc(lemburRows, "TransaksiApprovalID"),
        isHrd: true,
      });
      list.push({
        id: "promosi",
        label: "Promosi",
        columns: PROMOSI_COLUMNS,
        rows: sortDesc(promosiRows, "TransaksiApprovalID"),
        isHrd: true,
      });
      list.push({
        id: "kontrakkerja",
        label: "Kontrak Kerja",
        columns: KONTRAK_KERJA_COLUMNS,
        rows: sortDesc(kontrakKerjaRows, "TransaksiApprovalID"),
        isHrd: true,
      });
      list.push({
        id: "perjalanandinas",
        label: "Perjalanan Dinas",
        columns: PERJALANAN_DINAS_COLUMNS,
        rows: sortDesc(perjalananDinasRows, "TransaksiApprovalID"),
        isHrd: true,
      });
      list.push({
        id: "pdubah",
        label: "Perubahan Dinas",
        columns: PD_UBAH_COLUMNS,
        rows: sortDesc(pdUbahRows, "TransaksiApprovalID"),
        isHrd: true,
      });
      list.push({
        id: "pdlaporan",
        label: "Laporan Dinas",
        columns: PD_LAPORAN_COLUMNS,
        rows: sortDesc(pdLaporanRows, "TransaksiApprovalID"),
        isHrd: true,
      });
    }
    list.push({
      id: "jobrequest",
      label: "Job Request",
      columns: JOB_REQUEST_COLUMNS,
      rows: sortDesc(jrRows, "TransaksiApprovalID"),
      onRowClick: (i, navigate) =>
        navigate("/operasional-job-request-detail-v2", {
          state: { JrID: i.TransaksiApprovalID },
        }),
    });
    list.push({
      id: "quotation",
      label: "Quotation",
      columns: QUOTATION_COLUMNS,
      rows: sortDesc(qoRows, "TransaksiApprovalID"),
      onRowClick: (i, navigate) =>
        navigate("/quotation-detail-or-contract", {
          state: { QoNo: i.TransaksiApprovalID },
        }),
    });
    visibleNonHrd.forEach((s) => {
      list.push({
        id: s.id,
        label: s.label,
        columns: s.columns,
        rows: sortDesc(data[s.id] ?? [], s.sortKey),
        onRowClick: s.onRowClick,
        // Objek tab disalin field-per-field, jadi apa pun yang ditambahkan ke
        // NON_HRD_SOURCES harus disebut di sini juga — kalau tidak ia hilang
        // diam-diam dan fiturnya sekadar tak muncul, tanpa error.
        bulk: s.bulk,
      });
    });
    // Hanya tampilkan tab yang ada antreannya (badge >= 1); sembunyikan yang kosong
    return list.filter((t) => t.rows.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hrdVisible,
    hrdRows,
    lemburRows,
    promosiRows,
    kontrakKerjaRows,
    perjalananDinasRows,
    pdUbahRows,
    pdLaporanRows,
    jrRows,
    qoRows,
    visibleNonHrd,
    data,
  ]);

  const totalItems = tabs.reduce((sum, t) => sum + t.rows.length, 0);

  // Pilih tab default sekali: tab pertama yang ada isinya, jika tidak ada tab pertama
  useEffect(() => {
    if (autoSelected.current || loading || tabs.length === 0) return;
    const firstWithData = tabs.find((t) => t.rows.length > 0);
    setActiveTab((firstWithData ?? tabs[0]).id);
    autoSelected.current = true;
  }, [loading, tabs]);

  const handleTabClick = (id) => {
    autoSelected.current = true;
    setActiveTab(id);
    // Centang tidak boleh menyeberang tab: ID dari tab lain akan dikirim dengan
    // Jenis yang salah.
    setTerpilih([]);
    setBulkHasil(null);
  };

  // ── Approval borongan ──
  const bulkAktif = (tab) => canBulk && !!tab?.bulk;

  const toggleSatu = (id) =>
    setTerpilih((p) => {
      if (p.includes(id)) return p.filter((x) => x !== id);
      // Backend menolak lebih dari `bulkLimit` dengan 400. Menahannya di sini
      // membuat batas itu terasa sebagai batas saat mencentang, bukan sebagai
      // error yang baru muncul setelah tombol ditekan.
      if (p.length >= bulkLimit) return p;
      return [...p, id];
    });

  // "Pilih semua" berarti semua baris yang SEDANG TERLIHAT di tab ini — bukan
  // seluruh data. Mencentang yang tak terlihat berarti menyetujui tanpa melihat.
  // Jumlahnya dipotong `bulkLimit` karena itu batas satu aksi di backend.
  const idsDapatDicentang = (tab) =>
    tab.rows.map(tab.bulk.idOf).slice(0, bulkLimit);

  // Dipakai bersama oleh toggleSemua dan checkbox header supaya keduanya tak
  // pernah berbeda pendapat tentang arti "sudah tercentang semua".
  const semuaSudahTercentang = (tab) => {
    const ids = idsDapatDicentang(tab);
    return ids.length > 0 && ids.every((x) => terpilih.includes(x));
  };

  const toggleSemua = (tab) => {
    // Pembandingnya WAJIB daftar yang BISA dicentang (sudah dipotong
    // `bulkLimit`), bukan seluruh baris tab. Versi sebelumnya memakai seluruh
    // baris: pada tab berisi lebih dari `bulkLimit` dokumen, "semua tercentang"
    // tak pernah terpenuhi — checkbox header tak pernah tampak tercentang, dan
    // klik kedua mencentang ulang 25 yang sama alih-alih mengosongkan. Akibatnya
    // "All" kehilangan fungsi MENGHAPUS centang sama sekali.
    setTerpilih(semuaSudahTercentang(tab) ? [] : idsDapatDicentang(tab));
  };

  const jalankanBulk = async (tab) => {
    if (terpilih.length === 0) return;
    const ok = window.confirm(
      `Setujui ${terpilih.length} dokumen ${tab.label} sekaligus?\n\n` +
      `Dokumen tidak dibuka satu per satu. Item dan nilainya disetujui apa adanya, ` +
      `sesuai keputusan approver sebelumnya. Persetujuan tidak dapat dibatalkan.`
    );
    if (!ok) return;

    setBulkBusy(true);
    setBulkHasil(null);
    try {
      const res = await apiPostRaw("bulkApprove", {
        Jenis: tab.bulk.jenis,
        IDs: terpilih,
      });
      const hasil = res?.data ?? [];
      setBulkHasil(hasil);
      setTerpilih([]);

      const gagal = hasil.filter((h) => !h.Berhasil).length;
      if (gagal === 0) toast.success(`${hasil.length} dokumen disetujui.`);
      else toast.warn(`${hasil.length - gagal} berhasil, ${gagal} gagal — lihat rincian.`);

      // Muat ulang tab ini supaya baris yang sudah disetujui hilang dari antrean.
      const sumber = NON_HRD_SOURCES.find((s) => s.id === tab.id);
      if (sumber) {
        const rows = await sumber.fetch();
        setData((d) => ({ ...d, [tab.id]: rows }));
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message ?? "Gagal menjalankan approval borongan"
      );
    } finally {
      setBulkBusy(false);
    }
  };

  // Aksi approve/tolak Lembur dengan konfirmasi ulang + alasan wajib saat menolak.
  const submitLemburAksi = (status) => {
    const isTolak = status === "2";
    if (!alasanApproval.trim()) {
      window.alert(
        isTolak
          ? "Mohon isi alasan penolakan terlebih dahulu."
          : "Mohon isi alasan/catatan persetujuan terlebih dahulu."
      );
      return;
    }
    const ok = window.confirm(
      isTolak
        ? "Yakin MENOLAK pengajuan lembur ini? Tindakan tidak dapat dibatalkan."
        : "Yakin MENYETUJUI (Approve) pengajuan lembur ini?"
    );
    if (!ok) return;
    dispatch(
      aksiApproval({
        TransaksiApprovalID: currentID,
        Status: status,
        Revisi: alasanApproval,
      })
    ).then(() => dispatch(approveList({})));
    setShowModalLembur(false);
    setAlasanApproval("");
  };

  // Aksi approve/tolak Promosi/Mutasi/Demosi — pola sama persis submitLemburAksi.
  const submitPromosiAksi = (status) => {
    const isTolak = status === "2";
    if (!alasanApproval.trim()) {
      window.alert(
        isTolak
          ? "Mohon isi alasan penolakan terlebih dahulu."
          : "Mohon isi alasan/catatan persetujuan terlebih dahulu."
      );
      return;
    }
    const ok = window.confirm(
      isTolak
        ? "Yakin MENOLAK pengajuan Promosi/Mutasi ini? Tindakan tidak dapat dibatalkan."
        : "Yakin MENYETUJUI (Approve) pengajuan Promosi/Mutasi ini?"
    );
    if (!ok) return;
    dispatch(
      aksiApproval({
        TransaksiApprovalID: currentID,
        Status: status,
        Revisi: alasanApproval,
      })
    ).then(() => dispatch(approveList({})));
    setShowModalPromosi(false);
    setAlasanApproval("");
  };

  // Aksi approve/tolak Kontrak Kerja — pola sama persis submitLemburAksi.
  // Approve pada tahap final ikut meng-update MKaryawan.TipeKaryawanID di
  // backend (lihat AksiApprovalKontrakKerja) — tak ada aksi tambahan di sini.
  const submitKontrakKerjaAksi = (status) => {
    const isTolak = status === "2";
    if (!alasanApproval.trim()) {
      window.alert(
        isTolak
          ? "Mohon isi alasan penolakan terlebih dahulu."
          : "Mohon isi alasan/catatan persetujuan terlebih dahulu."
      );
      return;
    }
    const ok = window.confirm(
      isTolak
        ? "Yakin MENOLAK pengajuan Kontrak Kerja ini? Tindakan tidak dapat dibatalkan."
        : "Yakin MENYETUJUI (Approve) pengajuan Kontrak Kerja ini? Tipe karyawan akan langsung berubah bila ini approval tahap terakhir."
    );
    if (!ok) return;
    dispatch(
      aksiApproval({
        TransaksiApprovalID: currentID,
        Status: status,
        Revisi: alasanApproval,
      })
    ).then(() => dispatch(approveList({})));
    setShowModalKontrakKerja(false);
    setAlasanApproval("");
  };

  // Aksi approve/tolak Perjalanan Dinas — pola sama submitKontrakKerjaAksi.
  const submitPerjalananDinasAksi = (status) => {
    const isTolak = status === "2";
    if (!alasanApproval.trim()) {
      window.alert(
        isTolak
          ? "Mohon isi alasan penolakan terlebih dahulu."
          : "Mohon isi alasan/catatan persetujuan terlebih dahulu."
      );
      return;
    }
    const ok = window.confirm(
      isTolak
        ? "Yakin MENOLAK pengajuan Perjalanan Dinas ini? Pemohon dapat memperbaiki dan mengirim ulang."
        : "Yakin MENYETUJUI (Approve) pengajuan Perjalanan Dinas ini?"
    );
    if (!ok) return;
    dispatch(
      aksiApproval({
        TransaksiApprovalID: currentID,
        Status: status,
        Revisi: alasanApproval,
      })
    ).then(() => dispatch(approveList({})));
    setShowModalPerjalananDinas(false);
    setAlasanApproval("");
  };

  // Aksi approve/tolak dokumen turunan Perjalanan Dinas (Perubahan Agenda / Laporan).
  const submitPdSubAksi = (status) => {
    const isTolak = status === "2";
    if (!alasanApproval.trim()) {
      window.alert(
        isTolak
          ? "Mohon isi alasan penolakan terlebih dahulu."
          : "Mohon isi alasan/catatan persetujuan terlebih dahulu."
      );
      return;
    }
    const nama = pdSub.menu === "PerjalananDinasUbah" ? "Perubahan Agenda" : "Laporan Perjalanan Dinas";
    const ok = window.confirm(
      isTolak
        ? `Yakin MENOLAK ${nama} ini? Pemohon dapat memperbaiki dan mengirim ulang.`
        : `Yakin MENYETUJUI (Approve) ${nama} ini?`
    );
    if (!ok) return;
    dispatch(
      aksiApproval({ TransaksiApprovalID: currentID, Status: status, Revisi: alasanApproval })
    ).then(() => dispatch(approveList({})));
    setPdSub({ show: false, menu: "", spec: null });
    setAlasanApproval("");
  };

  // Klik baris HRD — buka modal detail yang sesuai (logika dari kartu lama)
  const handleHrdRowClick = (item) => {
    const menu = item.Menu;
    setCurrentID(item.TransaksiApprovalID);
    if (menu === "Sakit") {
      dispatch(getSpecSakit({ TransaksiApprovalID: item.TransaksiApprovalID }));
      setCurrentMenu("Sakit");
      setShowModalCuti(true);
    } else if (menu === "Cuti") {
      dispatch(getSpecCuti({ TransaksiApprovalID: item.TransaksiApprovalID }));
      setCurrentMenu("Cuti");
      setShowModalCuti(true);
    } else if (menu === "Tiket") {
      dispatch(getSpecTiket({ TransaksiApprovalID: item.TransaksiApprovalID }));
      setShowModalTiket(true);
    } else if (menu === "Resign") {
      dispatch(getSpecResign({ TransaksiApprovalID: item.TransaksiApprovalID }));
      setShowModalResign(true);
    } else if (menu === "PHK") {
      dispatch(getSpecPhk({ TransaksiApprovalID: item.TransaksiApprovalID }));
      setShowModalPhk(true);
    } else if (menu === "Pelatihan") {
      dispatch(
        getSpecPelatihan({ TransaksiApprovalID: item.TransaksiApprovalID })
      );
      setShowModalPelatihan(true);
    } else if (menu === "Lembur") {
      dispatch(getSpecLembur({ TransaksiApprovalID: item.TransaksiApprovalID }));
      setAlasanApproval("");
      setShowModalLembur(true);
    } else if (menu === "Izin") {
      dispatch(getSpecIzin({ TransaksiApprovalID: item.TransaksiApprovalID }));
      setShowModalIzin(true);
    } else if (menu === "Pengobatan") {
      dispatch(
        getSpecClaimPengobatan({
          TransaksiApprovalID: item.TransaksiApprovalID,
        })
      );
      setShowModalClaimPengobatan(true);
    } else if (menu === "Promosi") {
      dispatch(getSpecPromosi({ TransaksiApprovalID: item.TransaksiApprovalID }));
      setAlasanApproval("");
      setShowModalPromosi(true);
    } else if (menu === "KontrakKerja") {
      dispatch(
        getSpecKontrakKerja({ TransaksiApprovalID: item.TransaksiApprovalID })
      );
      setAlasanApproval("");
      setShowModalKontrakKerja(true);
    } else if (menu === "PerjalananDinasUbah" || menu === "PerjalananDinasLaporan") {
      setPdSub({ show: true, menu, spec: null });
      setAlasanApproval("");
      const ambil = menu === "PerjalananDinasUbah" ? getSpecUbah : getSpecLaporan;
      ambil(item.TransaksiApprovalID)
        .then((spec) => setPdSub({ show: true, menu, spec }))
        .catch(() => setPdSub({ show: false, menu: "", spec: null }));
    } else if (menu === "PerjalananDinas") {
      setPerjalananDinasSpec(null);
      setAlasanApproval("");
      setShowModalPerjalananDinas(true);
      getSpecPerjalananDinas(item.TransaksiApprovalID)
        .then(setPerjalananDinasSpec)
        .catch(() => setShowModalPerjalananDinas(false));
    }
  };

  if (loading) {
    return <Loading />;
  }

  const activeTabObj = tabs.find((t) => t.id === activeTab);

  return (
    <div className="md:ml-64">
      {/* Header halaman */}
      <div className="px-4 pt-6 pb-2">
        <h1 className="text-xl font-bold text-gray-800">Daftar Approval</h1>
        <p className="text-sm text-gray-500 mt-1">
          {totalItems > 0
            ? `${totalItems} item menunggu persetujuan`
            : "Tidak ada item yang perlu disetujui"}
        </p>
      </div>

      {tabs.length === 0 ? (
        <div className="w-full flex flex-col items-center justify-center py-16 text-gray-400">
          <svg
            className="w-16 h-16 mb-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
          <p className="text-lg font-medium">Semua sudah disetujui</p>
          <p className="text-sm mt-1">
            Tidak ada item yang menunggu persetujuan Anda saat ini.
          </p>
        </div>
      ) : (
        <>
          {/* Bar tab */}
          <div className="px-2 mt-2 border-b border-gray-200 overflow-x-auto">
            <div className="flex flex-nowrap gap-1 min-w-max">
              {tabs.map((t) => {
                const active = t.id === activeTab;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleTabClick(t.id)}
                    className={
                      "whitespace-nowrap px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors " +
                      (active
                        ? "border-blue-600 text-blue-600 bg-blue-50"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50")
                    }
                  >
                    {t.label}
                    <span
                      className={
                        "ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold " +
                        (t.rows.length > 0
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 text-gray-500")
                      }
                    >
                      {t.rows.length}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Isi tab aktif */}
          <div className="p-2">
            {activeTabObj && activeTabObj.rows.length > 0 ? (
              <>
                {/* Bilah aksi borongan — hanya untuk pemegang hak & tab yang
                    approve-nya bisa dijalankan tanpa membuka dokumen. */}
                {bulkAktif(activeTabObj) && (
                  <div className="flex items-center justify-between gap-3 mb-2 px-1">
                    <span className="text-xs text-gray-500">
                      {terpilih.length === 0
                        ? `Centang dokumen untuk disetujui sekaligus (maks. ${bulkLimit})`
                        : terpilih.length >= bulkLimit
                        ? `${terpilih.length} dokumen dipilih — batas ${bulkLimit} tercapai, hapus centang untuk memilih yang lain`
                        : `${terpilih.length} dokumen dipilih`}
                    </span>
                    <button
                      onClick={() => jalankanBulk(activeTabObj)}
                      disabled={terpilih.length === 0 || bulkBusy}
                      className="px-4 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-semibold disabled:opacity-50"
                    >
                      {bulkBusy
                        ? "Memproses…"
                        : `Setujui ${terpilih.length || ""} Terpilih`}
                    </button>
                  </div>
                )}

                {/* Rincian hasil per dokumen. Wajib per-baris: satu toast
                    "berhasil" akan menyembunyikan dokumen yang ditolak penjaga. */}
                {bulkHasil && bulkHasil.length > 0 && (
                  <div className="mb-2 border border-gray-200 rounded bg-white text-xs">
                    <div className="px-3 py-2 border-b font-semibold text-gray-600">
                      Hasil approval borongan
                    </div>
                    <ul className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                      {bulkHasil.map((h) => (
                        <li key={h.ID} className="px-3 py-1.5 flex gap-2">
                          <span className={h.Berhasil ? "text-green-600" : "text-red-600"}>
                            {h.Berhasil ? "✓" : "✕"}
                          </span>
                          <span className="text-gray-700">{h.ID}</span>
                          {!h.Berhasil && (
                            <span className="text-red-600">— {h.Pesan}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="overflow-x-auto border border-gray-200 rounded shadow-sm bg-white">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {bulkAktif(activeTabObj) && (
                          <th className="px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              checked={semuaSudahTercentang(activeTabObj)}
                              onChange={() => toggleSemua(activeTabObj)}
                            />
                          </th>
                        )}
                        <th className="px-4 py-3 w-10">#</th>
                        {activeTabObj.columns.map((c) => (
                          <th key={c.header} className="px-4 py-3">
                            {c.header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {activeTabObj.rows.map((item, idx) => (
                        <tr
                          key={idx}
                          className="cursor-pointer hover:bg-blue-50 transition-colors"
                          onClick={() =>
                            activeTabObj.isHrd
                              ? handleHrdRowClick(item)
                              : activeTabObj.onRowClick(item, navigate)
                          }
                        >
                          {bulkAktif(activeTabObj) && (
                            // stopPropagation wajib: seluruh <tr> ini clickable,
                            // tanpa itu mencentang justru berpindah halaman.
                            <td
                              className="px-4 py-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={terpilih.includes(activeTabObj.bulk.idOf(item))}
                                onChange={() =>
                                  toggleSatu(activeTabObj.bulk.idOf(item))
                                }
                              />
                            </td>
                          )}
                          <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                          {activeTabObj.columns.map((c) => (
                            <td
                              key={c.header}
                              className="px-4 py-3 text-gray-700"
                            >
                              {c.cell(item)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="w-full flex flex-col items-center justify-center py-16 text-gray-400">
                <svg
                  className="w-14 h-14 mb-4 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-base font-medium">
                  Tidak ada item pada {activeTabObj?.label}
                </p>
                <p className="text-sm mt-1">
                  Semua sudah disetujui untuk jenis ini.
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal Cuti / Sakit */}
      {showModalCuti &&
        (currentMenu === "Cuti"
          ? cuti.isSpecCutiSuccess && cuti.SpecCutiData?.TCutiID === currentID
          : sakit.isSpecSakitSuccess) &&
        (() => {
          const isSakit = currentMenu === "Sakit";
          const specData = isSakit ? sakit.SpecSakitData : cuti.SpecCutiData;
          const tanggalMulai = isSakit
            ? specData?.TanggalSakit
            : specData?.TanggalMulai;
          const tanggalAkhir = isSakit
            ? specData?.TanggalSembuh
            : specData?.TanggalAkhir;
          const keterangan = isSakit
            ? specData?.NamaPenyakit
            : specData?.KeperluanCuti;
          const jumlahHari = (() => {
            if (!tanggalMulai || !tanggalAkhir) return "-";
            const diff =
              Math.round(
                (new Date(tanggalAkhir) - new Date(tanggalMulai)) /
                  (1000 * 60 * 60 * 24)
              ) + 1;
            return diff > 0 ? diff + " hari" : "-";
          })();
          const formatTgl = (tgl) =>
            tgl ? new Date(tgl).toLocaleDateString("id-ID") : "";
          return (
            <Modal
              size="regular"
              active={showModalCuti}
              toggler={() => setShowModalCuti(true)}
            >
              <ModalHeader
                className="mt-20"
                toggler={() => setShowModalCuti(false)}
              >
                Detail {currentMenu}
              </ModalHeader>
              <ModalBody>
                <div className="flex flex-wrap -mx-3 mb-4">
                  <div className="w-full md:w-1/2 px-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      NIK
                    </label>
                    <input
                      value={specData?.Nik ?? ""}
                      disabled
                      className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                      type="text"
                    />
                  </div>
                  <div className="w-full md:w-1/2 px-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Nama
                    </label>
                    <input
                      value={specData?.NamaKaryawan ?? ""}
                      disabled
                      className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                      type="text"
                    />
                  </div>
                </div>
                {!isSakit && (
                  <div className="flex flex-wrap -mx-3 mb-4">
                    <div className="w-full md:w-1/2 px-3">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Jenis Cuti
                      </label>
                      <input
                        value={specData?.JenisCutiKet ?? ""}
                        disabled
                        className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                        type="text"
                      />
                    </div>
                    <div className="w-full md:w-1/2 px-3">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Periode Hak Cuti
                      </label>
                      <input
                        value={specData?.Periode || "-"}
                        disabled
                        className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                        type="text"
                      />
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap -mx-3 mb-4">
                  <div className="w-full md:w-1/3 px-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Tanggal Mulai
                    </label>
                    <input
                      value={formatTgl(tanggalMulai)}
                      disabled
                      className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                      type="text"
                    />
                  </div>
                  <div className="w-full md:w-1/3 px-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Tanggal Selesai
                    </label>
                    <input
                      value={formatTgl(tanggalAkhir)}
                      disabled
                      className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                      type="text"
                    />
                  </div>
                  <div className="w-full md:w-1/3 px-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Jumlah Hari
                    </label>
                    <input
                      value={jumlahHari}
                      disabled
                      className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                      type="text"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap -mx-3 mb-4">
                  <div className="w-full px-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Keterangan
                    </label>
                    <input
                      value={keterangan ?? ""}
                      disabled
                      className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                      type="text"
                    />
                  </div>
                </div>
                {!isSakit && (
                  <div className="flex flex-wrap -mx-3 mb-4">
                    <div className="w-full md:w-1/2 px-3">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Rekan Pengganti
                      </label>
                      <input
                        value={specData?.NamaPengganti ?? ""}
                        disabled
                        className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                        type="text"
                      />
                    </div>
                    <div className="w-full md:w-1/2 px-3">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        NIK Rekan Pengganti
                      </label>
                      <input
                        value={specData?.Pengganti ?? ""}
                        disabled
                        className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                        type="text"
                      />
                    </div>
                    {specData?.PenggantiBerhalang && (
                      <div className="w-full px-3 mt-3">
                        <div className="rounded border border-yellow-400 bg-yellow-50 text-yellow-800 text-sm px-4 py-3">
                          <span className="font-bold">Peringatan: </span>
                          Rekan pengganti {specData?.NamaPengganti} berhalangan,{" "}
                          {specData.PenggantiBerhalang}. Pertimbangkan sebelum
                          menyetujui.
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {!isSakit && (
                  <div className="flex flex-wrap -mx-3 mb-2">
                    <div className="w-full px-3 mb-3">
                      <div className="border-t border-gray-200" />
                    </div>
                    <div className="w-full md:w-1/3 px-3">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Saldo Awal
                      </label>
                      <input
                        value={specData?.HakCuti ?? "-"}
                        disabled
                        className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white text-center font-semibold"
                        type="text"
                      />
                    </div>
                    <div className="w-full md:w-1/3 px-3">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Diambil
                      </label>
                      <input
                        value={specData?.JmlCutiDiambil ?? "-"}
                        disabled
                        className="appearance-none block w-full text-gray-700 border border-red-300 rounded py-3 px-4 leading-tight outline-none bg-red-50 text-center font-semibold"
                        type="text"
                      />
                    </div>
                    <div className="w-full md:w-1/3 px-3">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Saldo Akhir
                      </label>
                      <input
                        value={specData?.SaldoAkhirCuti ?? "-"}
                        disabled
                        className="appearance-none block w-full text-gray-700 border border-green-300 rounded py-3 px-4 leading-tight outline-none bg-green-50 text-center font-semibold"
                        type="text"
                      />
                    </div>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  color="red"
                  buttonType="link"
                  onClick={() => setShowModalCuti(false)}
                  ripple="dark"
                >
                  Tutup
                </Button>
                <Button
                  color="red"
                  onClick={() => {
                    dispatch(
                      aksiApproval({
                        TransaksiApprovalID: currentID,
                        Status: "2",
                        Revisi: "",
                      })
                    );
                    setShowModalCuti(false);
                  }}
                  ripple="light"
                >
                  Tolak
                </Button>
                <Button
                  color="green"
                  onClick={() => {
                    dispatch(
                      aksiApproval({
                        TransaksiApprovalID: currentID,
                        Status: "1",
                        Revisi: "",
                      })
                    );
                    setShowModalCuti(false);
                  }}
                  ripple="light"
                >
                  Approve
                </Button>
              </ModalFooter>
            </Modal>
          );
        })()}

      {/* Modal Tiket */}
      {tiket.isSpecTiketSuccess && (
        <Modal
          size="regular"
          active={showModalTiket}
          toggler={() => setShowModalTiket(true)}
        >
          <ModalHeader
            className="mt-20"
            toggler={() => setShowModalTiket(false)}
          >
            Detail Tiket
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  NIK
                </label>
                <input
                  value={tiket.SpecTiketData?.Nik ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nama
                </label>
                <input
                  value={tiket.SpecTiketData?.NamaKaryawan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Keterangan
                </label>
                <input
                  value={tiket.SpecTiketData?.Keterangan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="red"
              buttonType="link"
              onClick={() => setShowModalTiket(false)}
              ripple="dark"
            >
              Tutup
            </Button>
            <Button
              color="red"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "2",
                    Revisi: "",
                  })
                );
                setShowModalTiket(false);
              }}
              ripple="light"
            >
              Tolak
            </Button>
            <Button
              color="green"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "1",
                    Revisi: "",
                  })
                );
                setShowModalTiket(false);
              }}
              ripple="light"
            >
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Modal Resign */}
      {resign.isSpecResignSuccess && (
        <Modal
          size="regular"
          active={showModalResign}
          toggler={() => setShowModalResign(true)}
        >
          <ModalHeader
            className="mt-20"
            toggler={() => setShowModalResign(false)}
          >
            Detail Resign
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  NIK
                </label>
                <input
                  value={resign.SpecResignData?.Nik ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nama
                </label>
                <input
                  value={resign.SpecResignData?.NamaKaryawan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Keterangan
                </label>
                <input
                  value={resign.SpecResignData?.Keterangan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="red"
              buttonType="link"
              onClick={() => setShowModalResign(false)}
              ripple="dark"
            >
              Tutup
            </Button>
            <Button
              color="red"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "2",
                    Revisi: "",
                  })
                );
                setShowModalResign(false);
              }}
              ripple="light"
            >
              Tolak
            </Button>
            <Button
              color="green"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "1",
                    Revisi: "",
                  })
                );
                setShowModalResign(false);
              }}
              ripple="light"
            >
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Modal PHK */}
      {phk.isSpecPhkSuccess && (
        <Modal
          size="regular"
          active={showModalPhk}
          toggler={() => setShowModalPhk(true)}
        >
          <ModalHeader className="mt-20" toggler={() => setShowModalPhk(false)}>
            Detail PHK
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  NIK
                </label>
                <input
                  value={phk.SpecPhkData?.Nik ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nama
                </label>
                <input
                  value={phk.SpecPhkData?.NamaKaryawan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Keterangan
                </label>
                <input
                  value={phk.SpecPhkData?.Keterangan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="red"
              buttonType="link"
              onClick={() => setShowModalPhk(false)}
              ripple="dark"
            >
              Tutup
            </Button>
            <Button
              color="red"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "2",
                    Revisi: "",
                  })
                );
                setShowModalPhk(false);
              }}
              ripple="light"
            >
              Tolak
            </Button>
            <Button
              color="green"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "1",
                    Revisi: "",
                  })
                );
                setShowModalPhk(false);
              }}
              ripple="light"
            >
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Modal Pelatihan */}
      {pelatihan.isSpecPelatihanSuccess && (
        <Modal
          size="regular"
          active={showModalPelatihan}
          toggler={() => setShowModalPelatihan(true)}
        >
          <ModalHeader
            className="mt-20"
            toggler={() => setShowModalPelatihan(false)}
          >
            Detail Pelatihan
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  NIK
                </label>
                <input
                  value={pelatihan.SpecPelatihanData?.Nik ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nama
                </label>
                <input
                  value={pelatihan.SpecPelatihanData?.NamaKaryawan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Keterangan
                </label>
                <input
                  value={pelatihan.SpecPelatihanData?.Keterangan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="red"
              buttonType="link"
              onClick={() => setShowModalPelatihan(false)}
              ripple="dark"
            >
              Tutup
            </Button>
            <Button
              color="red"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "2",
                    Revisi: "",
                  })
                );
                setShowModalPelatihan(false);
              }}
              ripple="light"
            >
              Tolak
            </Button>
            <Button
              color="green"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "1",
                    Revisi: "",
                  })
                );
                setShowModalPelatihan(false);
              }}
              ripple="light"
            >
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Modal Lembur */}
      {lembur.isSpecLemburSuccess && (
        <Modal
          size="regular"
          active={showModalLembur}
          toggler={() => setShowModalLembur(true)}
        >
          <ModalHeader
            className="mt-20"
            toggler={() => setShowModalLembur(false)}
          >
            Form Approval Lembur
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  NIK
                </label>
                <input
                  value={lembur.SpecLemburData?.Nik ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nama Pemohon
                </label>
                <input
                  value={lembur.SpecLemburData?.NamaKaryawan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Keperluan Lembur
                </label>
                <input
                  value={lembur.SpecLemburData?.KeperluanLembur ?? "-"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Mulai
                </label>
                <input
                  value={lembur.SpecLemburData?.TanggalMulai ?? "-"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Selesai
                </label>
                <input
                  value={lembur.SpecLemburData?.TanggalAkhir ?? "-"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Durasi Lembur
                </label>
                <input
                  value={formatDurasiLembur(
                    lembur.SpecLemburData?.TanggalMulai,
                    lembur.SpecLemburData?.TanggalAkhir
                  )}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white font-semibold"
                  type="text"
                />
              </div>
            </div>

            <div className="border-t mb-2 pt-3 font-semibold text-gray-700">
              Alur Approval
            </div>
            <div className="flex flex-wrap -mx-3 mb-4">
              {Array.isArray(lembur.SpecLemburData?.Approval) &&
              lembur.SpecLemburData.Approval.length > 0 ? (
                lembur.SpecLemburData.Approval.map((ap, ai) => (
                  <div key={ai} className="w-full md:w-1/2 px-3 mb-3">
                    <div className="border rounded border-gray-300 py-2 px-3 text-sm">
                      <div className="font-medium">
                        {ap.NamaBagian} - {ap.NamaJabatan}
                      </div>
                      <div
                        className={
                          ap.Approve === "Belum diApprove"
                            ? "text-yellow-600"
                            : ap.Approve === "Ditolak"
                            ? "text-red-600"
                            : "text-green-600"
                        }
                      >
                        {ap.Approve}
                      </div>
                      {ap.Approve !== "Belum diApprove" && ap.TanggalApproved ? (
                        <div className="text-xs text-gray-500">
                          {ap.TanggalApproved}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))
              ) : (
                <div className="w-full px-3 text-sm text-gray-400">-</div>
              )}
            </div>

            <div className="flex flex-wrap -mx-3">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Alasan / Catatan{" "}
                  <span className="text-red-500 normal-case">(wajib diisi)</span>
                </label>
                <textarea
                  value={alasanApproval}
                  onChange={(e) => setAlasanApproval(e.target.value)}
                  rows={3}
                  placeholder="Tulis alasan penolakan atau catatan persetujuan…"
                  className="appearance-none block w-full text-gray-700 border border-gray-300 rounded py-3 px-4 leading-tight outline-none bg-white focus:border-main-100"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="red"
              buttonType="link"
              onClick={() => {
                setShowModalLembur(false);
                setAlasanApproval("");
              }}
              ripple="dark"
            >
              Tutup
            </Button>
            <Button
              color="red"
              onClick={() => submitLemburAksi("2")}
              ripple="light"
            >
              Tolak
            </Button>
            <Button
              color="green"
              onClick={() => submitLemburAksi("1")}
              ripple="light"
            >
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Modal Promosi/Mutasi/Demosi — pola sama Lembur (form approval + alasan wajib) */}
      {promosi.isSpecPromosiSuccess && (
        <Modal
          size="regular"
          active={showModalPromosi}
          toggler={() => setShowModalPromosi(true)}
        >
          <ModalHeader
            className="mt-20"
            toggler={() => setShowModalPromosi(false)}
          >
            Form Approval Promosi/Mutasi
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  NIK
                </label>
                <input
                  value={promosi.SpecPromosiData?.Nik ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nama Pemohon
                </label>
                <input
                  value={promosi.SpecPromosiData?.NamaKaryawan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Jenis Transaksi
                </label>
                <input
                  value={
                    JENIS_TRANSAKSI_PROMOSI[promosi.SpecPromosiData?.JenisTransaksi] ??
                    dash(promosi.SpecPromosiData?.JenisTransaksi)
                  }
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Tanggal Efektif
                </label>
                <input
                  value={promosi.SpecPromosiData?.TanggalEfektif ?? "-"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Jabatan/Lokasi Lama
                </label>
                <input
                  value={promosi.SpecPromosiData?.JabatanLama ?? "-"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Jabatan/Lokasi Baru
                </label>
                <input
                  value={promosi.SpecPromosiData?.BagianBaru ?? "-"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white font-semibold"
                  type="text"
                />
              </div>
              <div className="w-full px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Alasan
                </label>
                <input
                  value={promosi.SpecPromosiData?.Alasan ?? "-"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>

            <div className="border-t mb-2 pt-3 font-semibold text-gray-700">
              Alur Approval
            </div>
            <div className="flex flex-wrap -mx-3 mb-4">
              {Array.isArray(promosi.SpecPromosiData?.Approval) &&
              promosi.SpecPromosiData.Approval.length > 0 ? (
                promosi.SpecPromosiData.Approval.map((ap, ai) => (
                  <div key={ai} className="w-full md:w-1/2 px-3 mb-3">
                    <div className="border rounded border-gray-300 py-2 px-3 text-sm">
                      <div className="font-medium">
                        {ap.NamaBagian} - {ap.NamaJabatan}
                      </div>
                      <div
                        className={
                          ap.Approve === "Belum diApprove"
                            ? "text-yellow-600"
                            : ap.Approve === "Ditolak"
                            ? "text-red-600"
                            : "text-green-600"
                        }
                      >
                        {ap.Approve}
                      </div>
                      {ap.Approve !== "Belum diApprove" && ap.TanggalApproved ? (
                        <div className="text-xs text-gray-500">
                          {ap.TanggalApproved}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))
              ) : (
                <div className="w-full px-3 text-sm text-gray-400">-</div>
              )}
            </div>

            <div className="flex flex-wrap -mx-3">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Alasan / Catatan{" "}
                  <span className="text-red-500 normal-case">(wajib diisi)</span>
                </label>
                <textarea
                  value={alasanApproval}
                  onChange={(e) => setAlasanApproval(e.target.value)}
                  rows={3}
                  placeholder="Tulis alasan penolakan atau catatan persetujuan…"
                  className="appearance-none block w-full text-gray-700 border border-gray-300 rounded py-3 px-4 leading-tight outline-none bg-white focus:border-main-100"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="red"
              buttonType="link"
              onClick={() => {
                setShowModalPromosi(false);
                setAlasanApproval("");
              }}
              ripple="dark"
            >
              Tutup
            </Button>
            <Button
              color="red"
              onClick={() => submitPromosiAksi("2")}
              ripple="light"
            >
              Tolak
            </Button>
            <Button
              color="green"
              onClick={() => submitPromosiAksi("1")}
              ripple="light"
            >
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Modal Kontrak Kerja — pola sama Lembur/Promosi (form approval + alasan wajib) */}
      {kontrakKerja.isSpecKontrakKerjaSuccess && (
        <Modal
          size="regular"
          active={showModalKontrakKerja}
          toggler={() => setShowModalKontrakKerja(true)}
        >
          <ModalHeader
            className="mt-20"
            toggler={() => setShowModalKontrakKerja(false)}
          >
            Form Approval Kontrak Kerja
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  NIK
                </label>
                <input
                  value={kontrakKerja.SpecKontrakKerjaData?.Nik ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nama Pemohon
                </label>
                <input
                  value={kontrakKerja.SpecKontrakKerjaData?.NamaKaryawan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nomor Kontrak
                </label>
                <input
                  value={kontrakKerja.SpecKontrakKerjaData?.NomorKontrak ?? "-"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Tipe Lama → Baru
                </label>
                <input
                  value={`${kontrakKerja.SpecKontrakKerjaData?.NamaTipeLama || "-"} → ${
                    kontrakKerja.SpecKontrakKerjaData?.NamaTipeKaryawan || "-"
                  }`}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white font-semibold"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Tanggal Mulai
                </label>
                <input
                  value={kontrakKerja.SpecKontrakKerjaData?.TanggalMulai ?? "-"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Tanggal Berakhir
                </label>
                <input
                  value={kontrakKerja.SpecKontrakKerjaData?.TanggalBerakhir || "Tanpa batas"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full px-3 mb-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Keterangan
                </label>
                <input
                  value={kontrakKerja.SpecKontrakKerjaData?.Keterangan ?? "-"}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>

            <div className="border-t mb-2 pt-3 font-semibold text-gray-700">
              Alur Approval
            </div>
            <div className="flex flex-wrap -mx-3 mb-4">
              {Array.isArray(kontrakKerja.SpecKontrakKerjaData?.Approval) &&
              kontrakKerja.SpecKontrakKerjaData.Approval.length > 0 ? (
                kontrakKerja.SpecKontrakKerjaData.Approval.map((ap, ai) => (
                  <div key={ai} className="w-full md:w-1/2 px-3 mb-3">
                    <div className="border rounded border-gray-300 py-2 px-3 text-sm">
                      <div className="font-medium">
                        {ap.NamaBagian} - {ap.NamaJabatan}
                      </div>
                      <div
                        className={
                          ap.Approve === "Belum diApprove"
                            ? "text-yellow-600"
                            : ap.Approve === "Ditolak"
                            ? "text-red-600"
                            : "text-green-600"
                        }
                      >
                        {ap.Approve}
                      </div>
                      {ap.Approve !== "Belum diApprove" && ap.TanggalApproved ? (
                        <div className="text-xs text-gray-500">
                          {ap.TanggalApproved}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))
              ) : (
                <div className="w-full px-3 text-sm text-gray-400">-</div>
              )}
            </div>

            <div className="flex flex-wrap -mx-3">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Alasan / Catatan{" "}
                  <span className="text-red-500 normal-case">(wajib diisi)</span>
                </label>
                <textarea
                  value={alasanApproval}
                  onChange={(e) => setAlasanApproval(e.target.value)}
                  rows={3}
                  placeholder="Tulis alasan penolakan atau catatan persetujuan…"
                  className="appearance-none block w-full text-gray-700 border border-gray-300 rounded py-3 px-4 leading-tight outline-none bg-white focus:border-main-100"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="red"
              buttonType="link"
              onClick={() => {
                setShowModalKontrakKerja(false);
                setAlasanApproval("");
              }}
              ripple="dark"
            >
              Tutup
            </Button>
            <Button
              color="red"
              onClick={() => submitKontrakKerjaAksi("2")}
              ripple="light"
            >
              Tolak
            </Button>
            <Button
              color="green"
              onClick={() => submitKontrakKerjaAksi("1")}
              ripple="light"
            >
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Modal Perjalanan Dinas — detail + alasan wajib + Approve/Tolak */}
      <Modal
        size="lg"
        active={showModalPerjalananDinas}
        toggler={() => setShowModalPerjalananDinas(true)}
      >
        <ModalHeader
          className="mt-20"
          toggler={() => setShowModalPerjalananDinas(false)}
        >
          Form Approval Perjalanan Dinas
        </ModalHeader>
        <ModalBody>
          {perjalananDinasSpec ? (
            <>
              <DetailPerjalananDinas d={perjalananDinasSpec} />
              <div className="mt-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Alasan / Catatan{" "}
                  <span className="text-red-500 normal-case">(wajib diisi)</span>
                </label>
                <textarea
                  value={alasanApproval}
                  onChange={(e) => setAlasanApproval(e.target.value)}
                  rows={3}
                  placeholder="Tulis alasan penolakan atau catatan persetujuan…"
                  className="appearance-none block w-full text-gray-700 border border-gray-300 rounded py-3 px-4 leading-tight outline-none bg-white focus:border-main-100"
                />
              </div>
            </>
          ) : (
            <Loading />
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="red"
            buttonType="link"
            onClick={() => {
              setShowModalPerjalananDinas(false);
              setAlasanApproval("");
            }}
            ripple="dark"
          >
            Tutup
          </Button>
          <Button
            color="red"
            onClick={() => submitPerjalananDinasAksi("2")}
            ripple="light"
            disabled={!perjalananDinasSpec}
          >
            Tolak
          </Button>
          <Button
            color="green"
            onClick={() => submitPerjalananDinasAksi("1")}
            ripple="light"
            disabled={!perjalananDinasSpec}
          >
            Approve
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Perjalanan Dinas Fase 2 — Perubahan Agenda / Laporan */}
      <Modal size="lg" active={pdSub.show} toggler={() => {}}>
        <ModalHeader
          className="mt-20"
          toggler={() => {
            setPdSub({ show: false, menu: "", spec: null });
            setAlasanApproval("");
          }}
        >
          {pdSub.menu === "PerjalananDinasUbah"
            ? "Form Approval Perubahan Agenda Dinas"
            : "Form Approval Laporan Perjalanan Dinas"}
        </ModalHeader>
        <ModalBody>
          {pdSub.spec ? (
            <>
              {pdSub.menu === "PerjalananDinasUbah" ? (
                <DetailUbah u={pdSub.spec} />
              ) : (
                <DetailLaporan l={pdSub.spec} />
              )}
              <div className="mt-4">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Alasan / Catatan{" "}
                  <span className="text-red-500 normal-case">(wajib diisi)</span>
                </label>
                <textarea
                  value={alasanApproval}
                  onChange={(e) => setAlasanApproval(e.target.value)}
                  rows={3}
                  placeholder="Tulis alasan penolakan atau catatan persetujuan…"
                  className="appearance-none block w-full text-gray-700 border border-gray-300 rounded py-3 px-4 leading-tight outline-none bg-white focus:border-main-100"
                />
              </div>
            </>
          ) : (
            <Loading />
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="red"
            buttonType="link"
            onClick={() => {
              setPdSub({ show: false, menu: "", spec: null });
              setAlasanApproval("");
            }}
            ripple="dark"
          >
            Tutup
          </Button>
          <Button color="red" onClick={() => submitPdSubAksi("2")} ripple="light" disabled={!pdSub.spec}>
            Tolak
          </Button>
          <Button color="green" onClick={() => submitPdSubAksi("1")} ripple="light" disabled={!pdSub.spec}>
            Approve
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Izin */}
      {izin.isSpecIzinSuccess && (
        <Modal
          size="regular"
          active={showModalIzin}
          toggler={() => setShowModalIzin(true)}
        >
          <ModalHeader className="mt-20" toggler={() => setShowModalIzin(false)}>
            Detail Izin
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  NIK
                </label>
                <input
                  value={izin.SpecIzinData?.Nik ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nama
                </label>
                <input
                  value={izin.SpecIzinData?.NamaKaryawan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Keterangan
                </label>
                <input
                  value={izin.SpecIzinData?.Keterangan ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="red"
              buttonType="link"
              onClick={() => setShowModalIzin(false)}
              ripple="dark"
            >
              Tutup
            </Button>
            <Button
              color="red"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "2",
                    Revisi: "",
                  })
                );
                setShowModalIzin(false);
              }}
              ripple="light"
            >
              Tolak
            </Button>
            <Button
              color="green"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "1",
                    Revisi: "",
                  })
                );
                setShowModalIzin(false);
              }}
              ripple="light"
            >
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Modal Pengobatan */}
      {claimPengobatan.isSpecClaimPengobatanSuccess && (
        <Modal
          size="regular"
          active={showModalClaimPengobatan}
          toggler={() => setShowModalClaimPengobatan(true)}
        >
          <ModalHeader
            className="mt-20"
            toggler={() => setShowModalClaimPengobatan(false)}
          >
            Detail Pengobatan
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  NIK
                </label>
                <input
                  value={claimPengobatan.SpecClaimPengobatanData?.Nik ?? ""}
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Nama
                </label>
                <input
                  value={
                    claimPengobatan.SpecClaimPengobatanData?.NamaKaryawan ?? ""
                  }
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
            <div className="flex flex-wrap -mx-3 mb-4">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Keterangan
                </label>
                <input
                  value={
                    claimPengobatan.SpecClaimPengobatanData?.Keterangan ?? ""
                  }
                  disabled
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white"
                  type="text"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="red"
              buttonType="link"
              onClick={() => setShowModalClaimPengobatan(false)}
              ripple="dark"
            >
              Tutup
            </Button>
            <Button
              color="red"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "2",
                    Revisi: "",
                  })
                );
                setShowModalClaimPengobatan(false);
              }}
              ripple="light"
            >
              Tolak
            </Button>
            <Button
              color="green"
              onClick={() => {
                dispatch(
                  aksiApproval({
                    TransaksiApprovalID: currentID,
                    Status: "1",
                    Revisi: "",
                  })
                );
                setShowModalClaimPengobatan(false);
              }}
              ripple="light"
            >
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default ApprovalListPage;
